import { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";

// ── Styled Components ────────────────────────────────────────────────────────

const Wrapper = styled.div`
  position: relative;
  width: 200px;
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
`;

const TagsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px 10px;
  min-height: 44px;
  width: 200px;
  background: rgba(255, 255, 255, 0.13);
  border: 1px solid rgba(7, 7, 7, 0.25);
  border-radius: 12px;
  align-items: center;
  cursor: pointer;
  box-sizing: border-box;
`;

const Tag = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 30px 6px 12px;
  border-radius: 999px;
  background: rgb(59 130 246);
  color: white;
  font-size: 12px;
  font-weight: 600;
  user-select: none;
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RemoveBtn = styled.button`
  position: absolute;
  right: 4px;
  top: 4px;
  border: none;
  background: black;
  color: white;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  z-index: 1;

  &:hover {
    background: #fff;
    color: black;
  }
`;

const Placeholder = styled.span`
  color: #6b7280;
  font-size: 14px;
`;

// Portal dropdown — rendered into document.body
const PortalDropdown = styled.div`
  position: fixed;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e4e8f0;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.14);
  max-height: 220px;
  overflow-y: auto;
  z-index: 99999;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
`;

const OptionRow = styled.button`
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  border: none;
  background: ${({ $selected }) =>
    $selected ? "rgba(14,165,233,0.12)" : "transparent"};
  color: ${({ $selected }) => ($selected ? "#0369a1" : "#111827")};
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: ${({ $selected }) =>
      $selected ? "rgba(14,165,233,0.18)" : "rgba(0,0,0,0.04)"};
  }
`;

const CheckDot = styled.span`
  width: 8px;
  height: 8px;
  flex-shrink: 0;
  border-radius: 999px;
  background: ${({ $selected }) => ($selected ? "#0ea5e9" : "transparent")};
  outline: 2px solid ${({ $selected }) => ($selected ? "#0ea5e9" : "rgba(0,0,0,0.25)")};
  outline-offset: 2px;
`;

const HiddenSelect = styled.select`
  display: none;
`;
function stripExt(filename) {
  return filename.replace(/\.[^/.]+$/, "");
}
// ── Component ────────────────────────────────────────────────────────────────

export default function SelectorSenario({
  id = "scenarios",
  options,
  value,
  defaultValue = [],
  onChange,
  placeholder = "Select a scenario",
}) {
  const isControlled = Array.isArray(value);
  const [internal, setInternal] = useState(defaultValue);
  const selected = isControlled ? value : internal;

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const tagsRef = useRef(null);
  const dropdownRef = useRef(null);

  // Recalculate position on open
  useEffect(() => {
    if (open && tagsRef.current) {
      const rect = tagsRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (
        tagsRef.current &&
        !tagsRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const selectedOptions = useMemo(
    () => options.filter((o) => selected.includes(o.value)),
    [options, selected],
  );

  const setValues = (next) => {
    if (!isControlled) setInternal(next);
    onChange?.(next);
  };

  const toggle = (val) => {
    const next = selected.includes(val)
      ? selected.filter((v) => v !== val)
      : [...selected, val];
    setValues(next);
  };

  const dropdown = open && ReactDOM.createPortal(
    <PortalDropdown
      ref={dropdownRef}
      $top={pos.top}
      $left={pos.left}
      $width={pos.width}
      role="listbox"
      aria-multiselectable="true"
    >
      {options.map((opt) => {
        const sel = selected.includes(opt.value);
        return (
          <OptionRow
            key={opt.value}
            type="button"
            $selected={sel}
            aria-selected={sel}
            onClick={() => toggle(opt.value)}
          >
            <CheckDot $selected={sel} />
            {stripExt(opt.label)}
          </OptionRow>
        );
      })}
    </PortalDropdown>,
    document.body,
  );

  return (
    <Wrapper>
      <TagsBar
        ref={tagsRef}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {selectedOptions.length === 0 ? (
          <Placeholder>{placeholder}</Placeholder>
        ) : (
          selectedOptions.map((opt) => (
            <Tag key={opt.value}>
              {stripExt(opt.label)}
              <RemoveBtn
                aria-label={`Remove ${stripExt(opt.label)}`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(opt.value);
                }}
              >
                ×
              </RemoveBtn>
            </Tag>
          ))
        )}
      </TagsBar>

      {dropdown}

      <HiddenSelect id={id} multiple value={selected} readOnly>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </HiddenSelect>
    </Wrapper>
  );
}